import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { CerereTypeService } from '../cerere-type.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { activeUsersByOrganigramaPayload } from '../../users-settings/user.interface';
import {
    CerereType,
    CerereTypeAddPayload,
    CerereTypeUpdatePayload,
} from '../cerere-type.interface';
import { Observable, map, finalize, Subject, of, switchMap, distinctUntilChanged, takeUntil, tap, catchError } from 'rxjs';
import { DirectiiService } from '../../directii/directii.service';
import { CompartimenteService } from '../../compartimente/compartimente.service';
import { CerereStatusService } from '../../cerere-status/cerere-status.service';
import { CererePriorityService } from '../../cerere-priority/cerere-priority.service';
import { UsersService } from '../../users-settings/users.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';

@Component({
    selector: 'app-edit-cerere-type',
    templateUrl: './edit-cerere-type.component.html',
    styleUrls: ['./edit-cerere-type.component.scss'],
})
export class EditCerereTypeComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.name, [Validators.required]),
        slug: new FormControl(this.data.slug, [Validators.required]),
        priority_id: new FormControl(this.data.priority_id, [Validators.required, notDefaultValidator('-1')]),
        status_id: new FormControl(this.data.status_id, [Validators.required, notDefaultValidator('-1')]),
        directie_id: new FormControl(this.data.directie_id, [Validators.required, notDefaultValidator('-1')]),
        compartiment_id: new FormControl(this.data.compartiment_id, []),
        user_id: new FormControl(this.data.user_id, []),
        observations: new FormControl(this.data.observations),
        indoc_name: new FormControl(this.data.indoc_name),
        indoc_id: new FormControl(this.data.indoc_id),
    });

    directiiActive: Generic[] = [];
    compartimenteActive!: Generic[];
    usersActive!: Generic[];
    statusesActive$!: Observable<Generic[]>;
    prioritiesActive$!: Observable<Generic[]>;

    private editDestroy$ = new Subject<void>();

    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: CerereType,
        private dialogRef: MatDialogRef<EditCerereTypeComponent>,
        private cerereTypeService: CerereTypeService,
        public directiiService: DirectiiService,
        public compartimenteService: CompartimenteService,
        public cerereStatusService: CerereStatusService,
        public cererePriorityService: CererePriorityService,
        public usersService: UsersService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private fb: FormBuilder,
    ) {
        this.processToken();
    }

    ngOnInit() {
        this.loadDirectiiActive();
        this.loadStatususesActive();
        this.loadPrioritiesActive();
        this.renderFieldsSearch();

        if (this.data.directie_id) {
            this.loadCompartimenteActive(this.data.directie_id);
        }

        this.loadUsersActive();
    }

    ngOnDestroy(): void {
        this.editDestroy$.next();
        this.editDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.cerereTypeService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            compartiment_id,
            user_id,
            observations,
            indoc_name,
            indoc_id,
        } = this.form.value as CerereTypeAddPayload;
        const updateCerereType: CerereTypeUpdatePayload = {
            id: this.data.id,
            name,
            slug,
            priority_id,
            status_id,
            user_id,
            directie_id,
            compartiment_id,
            observations,
            indoc_id,
            indoc_name,
        };

        this.cerereTypeService
            .updateRequestType(updateCerereType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onHttpError(res: any) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                this.notificationService.warningSwal(
                    this.errorTitle,
                    msg,
                    this.errorIcon
                );
            });
        } else {
            let msg;
            try {
                // try to catch the message from server
                msg = res.error.errors.message[0];
            } catch (error) {
                // controll output message
                msg = helper.dialogConfig.generalMessages.error;
            }
            this.notificationService.warningSwal(
                this.errorTitle,
                msg,
                this.errorIcon
            );
        }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.directiiService
            .getDirectiiActive()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.directiiActive = res;
                    this.filteredDirectii = res;
                },
                error: (err) => {
                    console.error('Error in get directii active:', err);
                }
            });
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['user_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.compartimenteService.getCompartimenteByDirectieId(directieId)
            : this.compartimenteService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimenteActive = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        });
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['user_id'].patchValue(-1);

        if (compartimentId) {
            this.loadUsersActive();
        }
    }

    loadUsersActive() {
        this.isLoading = true;
        let formValues = { ...this.form.value };
        let formValuesForm: FormGroup = this.fb.group(formValues);

        if (formValuesForm.get('compartiment_id')?.value === '-1') {
            formValuesForm.get('compartiment_id')?.setValue(null);
        }

        this.usersService
            .getActiveUsersByOrganigrama(formValuesForm.value)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.form.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadStatususesActive() {
        this.isLoading = true;
        this.statusesActive$ = this.cerereStatusService.getActiveStatus().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    loadPrioritiesActive() {
        this.isLoading = true;
        this.prioritiesActive$ = this.cererePriorityService
            .getActiveCererePriorities()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }
}
